import { FC, memo, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import LicenseContext from 'src/contexts/LicenseContext';
import { usePersistedJSON } from 'src/hooks/usePersistedJSON';
import Component from 'src/pages/TruckShifts/Component';
import { buildTruckShiftsPath, canonicalRangeFromUrl, isPeriodMode, PeriodMode, PERIOD_STORAGE_KEY } from 'src/pages/TruckShifts/truckShiftGrid';

const TruckShifts: FC = memo(() => {
  const { startOn, endOn, backTo } = useParams();
  const context = useContext(LicenseContext);
  const navigate = useNavigate();
  const [resolved, setResolved] = useState<{ startOn: string; endOn: string; period: PeriodMode } | null>(null);
  // 週/月 表示モードをセッション跨ぎで永続化。範囲未指定の初回入場時の既定に使う。
  const [preferredPeriod, setPreferredPeriod] = usePersistedJSON<PeriodMode>(PERIOD_STORAGE_KEY, 'week', isPeriodMode);

  useEffect(() => {
    if (!context.config?.on) return;

    // 不正・半端・未指定の URL も canonicalRangeFromUrl が fallbackAnchor(=config.on) で寄せる。
    // 範囲未指定の初回は永続化した preferredPeriod を既定に、範囲明示 URL は period を URL から導出（共有リンク優先）。
    // 404 は出さず、常に canonical な URL へ replace してから描画する（設計書 URL⇔period 復元契約）。
    const { period, range } = canonicalRangeFromUrl(startOn, endOn, context.config.on, preferredPeriod);
    const path = buildTruckShiftsPath({ startOn: range.startOn, endOn: range.endOn, backTo });

    if (range.startOn !== startOn || range.endOn !== endOn) {
      void navigate(path, { replace: true });
      return;
    }
    setResolved({ ...range, period });
  }, [startOn, endOn, backTo, context.config, preferredPeriod]);

  // 表示モードが確定・変化したら永続化（次回の初回既定に反映）。
  const resolvedPeriod = resolved?.period;
  useEffect(() => {
    if (resolvedPeriod) setPreferredPeriod(resolvedPeriod);
  }, [resolvedPeriod, setPreferredPeriod]);

  if (!resolved) return null;

  return <Component startOn={resolved.startOn} endOn={resolved.endOn} period={resolved.period} backTo={backTo} />;
});

export default TruckShifts;
